// Cart/order lines only carry the master item_name, but an upsell line should
// read as the upsell's display_name. The API has no field for it, so look it
// up on the referring item's public platform-view (cached per item).
import { useStoreContextStore } from '@core/store/storeContextStore';
import { getProduct } from './catalogService';

const TTL = 5 * 60 * 1000;
const cache = new Map(); // `${accountId}|${locationId}|${refId}` -> { at, names }

function namesFor(refId, ctx) {
  const key = `${ctx.accountId}|${ctx.locationId}|${refId}`;
  const hit = cache.get(key);
  if (hit && Date.now() - hit.at < TTL) return hit.names;
  const names = getProduct(refId, ctx)
    .then((p) => {
      const upsells = [
        ...(p?.upsell_group_details ?? []).flatMap((g) => g?.upsell_items ?? []),
        ...(p?.single_upsell_item_details ?? []),
      ];
      return new Map(
        upsells
          .filter((u) => u?.upsell_item_id)
          .map((u) => [u.upsell_item_id, u.display_name || u.item_name]),
      );
    })
    .catch(() => {
      cache.delete(key);
      return new Map();
    });
  cache.set(key, { at: Date.now(), names });
  return names;
}

/** Renames upsell lines to their upsell display_name; other lines untouched. */
export async function withUpsellNames(lines = [], locationId) {
  const store = useStoreContextStore.getState();
  const ctx = {
    accountId: store.accountId,
    locationId: locationId || store.locationId,
    channelId: store.channelId,
    priceField: store.priceField,
  };
  const refIds = [
    ...new Set(lines.filter((l) => l.upsellItemId).map((l) => l.upsellingRefId)),
  ].filter(Boolean);
  if (!refIds.length) return lines;
  const maps = await Promise.all(refIds.map((id) => namesFor(id, ctx)));
  const byRef = Object.fromEntries(refIds.map((id, i) => [id, maps[i]]));
  return lines.map((l) => {
    const name = byRef[l.upsellingRefId]?.get(l.upsellItemId);
    return name ? { ...l, itemName: name } : l;
  });
}

export const clearUpsellNameCache = () => cache.clear();
